import axios from 'axios';
import { mapGoogleAddressComponentsToFormAddress } from './googleAddressComponents';

function getDocketGeoBaseUrl() {
  const base = process.env.REACT_APP_DOCKET_GEO_URL;
  if (!base || typeof base !== 'string') {
    return '';
  }
  return base.replace(/\/$/, '');
}

/**
 * Tenancy query param name. Docket production uses `k`; server also accepts `t` or `teamId`.
 * @see REACT_APP_DOCKET_GEO_TEAM_QUERY_KEY
 */
function getTeamQueryKey() {
  return process.env.REACT_APP_DOCKET_GEO_TEAM_QUERY_KEY || 'k';
}

function getTeamQueryValue(company) {
  if (!company || typeof company !== 'object') {
    return undefined;
  }
  return company.teamId || company.team_id || company.companyId || company.id;
}

function optionalGeoHeaders() {
  const headers = {};
  const token = process.env.REACT_APP_DOCKET_GEO_BEARER_TOKEN;
  if (token && typeof token === 'string') {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

function unwrapPayload(data) {
  if (data == null) {
    return null;
  }
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch (e) {
      return null;
    }
  }
  if (typeof data === 'object' && data.data !== undefined && data.data !== data) {
    return unwrapPayload(data.data);
  }
  return data;
}

function appendTeamParams(params, company) {
  const teamKey = getTeamQueryKey();
  const teamVal = getTeamQueryValue(company);
  if (teamVal !== undefined && teamVal !== null && teamVal !== '') {
    params[teamKey] = teamVal;
  }
}

/**
 * httpApiGeo autocomplete: `{ valid: true, results: { predictions: [...], status } }`.
 * If `valid === false`, returns [].
 * @param {object} payload — parsed JSON body from Docket httpApiGeo
 * @returns {Array<{ place_id: string, description: string, structured_formatting?: object }>}
 */
export function extractPredictionsFromDocketGeo(payload) {
  const d = unwrapPayload(payload);
  if (!d || typeof d !== 'object') {
    return [];
  }
  if (d.valid === false) {
    return [];
  }
  if (Array.isArray(d.predictions)) {
    return d.predictions;
  }
  const results = d.results;
  if (results && typeof results === 'object' && !Array.isArray(results) && Array.isArray(results.predictions)) {
    return results.predictions;
  }
  if (d.body && typeof d.body === 'string') {
    const inner = unwrapPayload(d.body);
    if (inner && inner.valid === false) {
      return [];
    }
    if (inner && Array.isArray(inner.predictions)) {
      return inner.predictions;
    }
    if (inner && inner.results && typeof inner.results === 'object' && !Array.isArray(inner.results)) {
      if (Array.isArray(inner.results.predictions)) {
        return inner.results.predictions;
      }
    }
  }
  return [];
}

/**
 * Normalize httpApiGeo "resolved address" payloads (place_id, geocode with full=true, etc.).
 * If `valid === false`, returns null.
 * @param {object} payload
 */
export function extractResolvedAddressFromDocketGeo(payload) {
  const d = unwrapPayload(payload);
  if (!d || typeof d !== 'object') {
    return null;
  }
  if (d.valid === false) {
    return null;
  }

  if (d.addressLine1 !== undefined || d.city !== undefined || d.state !== undefined) {
    const loc = d.location;
    let location;
    if (loc && typeof loc === 'object') {
      if (loc.latitude !== undefined && loc.longitude !== undefined) {
        location = { latitude: loc.latitude, longitude: loc.longitude };
      } else if (loc.lat !== undefined && loc.lng !== undefined) {
        location = { latitude: loc.lat, longitude: loc.lng };
      }
    }
    return {
      addressLine1: d.addressLine1,
      city: d.city,
      state: d.state,
      zip: d.zip || d.postal || d.postalCode,
      county: d.county,
      countyVerbose: d.countyVerbose,
      countryCode: d.countryCode || d.country,
      location: location || d.location,
    };
  }

  if (d.resolved) {
    return extractResolvedAddressFromDocketGeo(d.resolved);
  }

  if (d.result) {
    return mapGoogleAddressComponentsToFormAddress(d.result);
  }

  const results = d.results;
  if (results != null) {
    if (Array.isArray(results) && results[0]) {
      const first = results[0];
      if (first && first.address_components) {
        return mapGoogleAddressComponentsToFormAddress(first);
      }
      if (first && typeof first === 'object') {
        return extractResolvedAddressFromDocketGeo(first);
      }
    }
    if (typeof results === 'object' && !Array.isArray(results) && results.address_components) {
      return mapGoogleAddressComponentsToFormAddress(results);
    }
  }

  return null;
}

function buildAutocompleteParams(input, company, locationBias) {
  const params = {
    event_type: 'autocomplete',
    input: input.trim(),
  };
  appendTeamParams(params, company);
  if (
    locationBias &&
    typeof locationBias.lat === 'number' &&
    typeof locationBias.lng === 'number' &&
    !Number.isNaN(locationBias.lat) &&
    !Number.isNaN(locationBias.lng)
  ) {
    params.location = `${locationBias.lat},${locationBias.lng}`;
  }
  return params;
}

function buildPlaceIdParams(placeId, company) {
  const params = {
    event_type: 'autocomplete',
    place_id: placeId,
  };
  appendTeamParams(params, company);
  return params;
}

/**
 * event_type=geocode: server expects address + zip (+ full=true), or fullAddress + full=true, or lat + lng.
 * @param {{ address: string, zip: string } | { fullAddress: string }} query
 * @param {object | undefined} company
 */
function buildGeocodeParams(query, company) {
  const eventType = process.env.REACT_APP_DOCKET_GEO_ZIP_EVENT_TYPE || 'geocode';
  const params = {
    event_type: eventType,
    full: 'true',
  };
  appendTeamParams(params, company);

  if (query && typeof query.fullAddress === 'string' && query.fullAddress.trim() !== '') {
    params.fullAddress = query.fullAddress.trim();
    return params;
  }

  const address = (query && query.address) || '';
  const zip = (query && query.zip) || '';
  params.address = typeof address === 'string' ? address.trim() : '';
  params.zip = typeof zip === 'string' ? zip.trim() : '';
  return params;
}

export function isDocketGeoConfigured() {
  return getDocketGeoBaseUrl() !== '';
}

/**
 * @param {string} input
 * @param {object | undefined} company
 * @param {{ lat: number, lng: number } | undefined} locationBias
 * @param {AbortSignal | undefined} signal
 */
export async function fetchDocketAutocomplete(input, company, locationBias, signal) {
  const base = getDocketGeoBaseUrl();
  if (!base || input.trim().length < 3) {
    return [];
  }
  const { data } = await axios.get(base, {
    params: buildAutocompleteParams(input, company, locationBias),
    signal,
    headers: optionalGeoHeaders(),
  });
  return extractPredictionsFromDocketGeo(data);
}

/**
 * @param {string} placeId
 * @param {object | undefined} company
 * @param {AbortSignal | undefined} signal
 */
export async function fetchDocketPlaceResolved(placeId, company, signal) {
  const base = getDocketGeoBaseUrl();
  if (!base || !placeId) {
    return null;
  }
  const { data } = await axios.get(base, {
    params: buildPlaceIdParams(placeId, company),
    signal,
    headers: optionalGeoHeaders(),
  });
  return extractResolvedAddressFromDocketGeo(data);
}

/**
 * Zip / street geocode (`event_type=geocode`): send `address` + `zip` + `full=true`, or `fullAddress` + `full=true`.
 * @param {{ address: string, zip: string } | { fullAddress: string }} query
 * @param {object | undefined} company
 * @param {AbortSignal | undefined} signal
 */
export async function fetchDocketZipGeocode(query, company, signal) {
  const base = getDocketGeoBaseUrl();
  if (!base || !query || typeof query !== 'object') {
    return null;
  }
  const hasPair =
    typeof query.address === 'string' &&
    typeof query.zip === 'string' &&
    (query.address.trim() !== '' || query.zip.trim() !== '');
  const hasFull = typeof query.fullAddress === 'string' && query.fullAddress.trim() !== '';
  if (!hasPair && !hasFull) {
    return null;
  }
  const { data } = await axios.get(base, {
    params: buildGeocodeParams(query, company),
    signal,
    headers: optionalGeoHeaders(),
  });
  return extractResolvedAddressFromDocketGeo(data);
}
